/**
 * GatewaySettingsModal — add or edit a BYOM model registration.
 *
 * Each registration represents one model with its own API key, run URL,
 * capabilities, and an optional "Make Default" flag. Multiple registrations
 * per user are allowed.
 *
 * Feature tag: `gateway-settings`. NAD trace command:
 *
 *     scripts/nad trace --feature gateway-settings --last 10m
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { APP_NAME } from '../../constants/brand'
import { useCustomProtocolUiEnabled } from '../../hooks/useCustomProtocolUiEnabled'
import Modal from '../ui/Modal'
import {
  ApiRequestError,
  createGatewayConfig,
  getVideoByomPresets,
  updateGatewayConfig,
  testGatewayConnection,
  testGatewayCredentials,
  type ApiValue,
  type DirectorByomProvider,
  type GatewayConfig,
  type GatewayConfigKind,
  type TestConnectionResult,
  type TestCredentialsResult,
  type VideoByomPreset,
  type VideoByomPresetId,
} from '../../api/client'
import { createNadDebug } from '../../utils/debugLog'
import { countBucket } from '../../analytics/buckets'
import { track } from '../../analytics/events'
import type { ByomEntryPoint } from '../../analytics/generated/analyticsEvents'

const log = createNadDebug('gateway-settings')

// ---------------------------------------------------------------------------
// URL validation — matches the backend Pydantic validator
// Returns a translation key on failure, null on success.
// ---------------------------------------------------------------------------

function validateUrl(url: string): string | null {
  const trimmed = url.trim()
  if (!trimmed) return null  // empty is allowed (optional field)
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    if (/^[^\s/]+\.[^\s/]+/.test(trimmed)) {
      return 'gatewayModal.urlHttpsRequired'
    }
    return 'gatewayModal.urlInvalidFormat'
  }
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'https:') {
      return 'gatewayModal.urlHttpsRequired'
    }
    if (!parsed.hostname.includes('.')) {
      return 'gatewayModal.urlInvalidHostname'
    }
    return null  // valid
  } catch {
    return 'gatewayModal.urlInvalidFormat'
  }
}

function isFalCredentialsUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim())
    const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '')
    return hostname === 'fal.run' || hostname.endsWith('.fal.run') ||
      hostname === 'fal.ai' || hostname.endsWith('.fal.ai')
  } catch {
    return false
  }
}

function hostMatches(url: string, hosts: string[]): boolean {
  try {
    const parsed = new URL(url.trim())
    const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '')
    return hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`))
  } catch {
    return false
  }
}

function supportsDirectorKeyTest(url: string, provider: DirectorByomProvider): boolean {
  if (provider === 'openai_chat_completions') {
    return hostMatches(url, ['api.openai.com'])
  }
  if (provider === 'anthropic_messages') {
    return hostMatches(url, ['api.meta.ai'])
  }
  return false
}

// ---------------------------------------------------------------------------
// Default capabilities for new registrations
// ---------------------------------------------------------------------------

const BYTEPLUS_RUN_URL_EXAMPLE = 'https://ark.ap-southeast.bytepluses.com/api/v3/contents/generations/tasks'
const DEFAULT_VIDEO_MODEL_NAME = 'My Ref-to-Video Model'
const DEFAULT_VIDEO_REQUEST_PATTERN = 'pattern_byteplus'
const DEFAULT_DURATION_SECONDS = Array.from({ length: 30 }, (_, index) => String(index + 1))
const DEFAULT_MAX_IMAGE_REFERENCES = 50
type CapabilityMap = Record<string, string[] | string>

// New registrations default to the current BytePlus multimodal contract. The
// selected provider model remains user-owned BYOM routing; these values only
// drive Dreamwrite's duration picker and reference guards.
const DEFAULT_CAPABILITIES: CapabilityMap = {
  duration_range: DEFAULT_DURATION_SECONDS,
  aspect_ratios: ['16:9', '9:16'],
  resolutions: ['480p', '720p', '1080p'],
  audio_control: ['with_audio', 'no_audio'],
  reference_images_support: [String(DEFAULT_MAX_IMAGE_REFERENCES)],
  reference_videos_support: ['1', '2', '3'],
  reference_audios_support: ['1', '2', '3'],
}

const DEFAULT_DIRECTOR_PROVIDER: DirectorByomProvider = 'anthropic_messages'
const DEFAULT_DIRECTOR_MODEL = 'muse-spark-1.1'
const DEFAULT_DIRECTOR_BASE_URL = 'https://api.meta.ai/v1'
const DEFAULT_DIRECTOR_NAME = 'Muse Spark'
const DEFAULT_DIRECTOR_PRESET: DirectorModelPreset = {
  keyName: DEFAULT_DIRECTOR_NAME,
  baseUrl: DEFAULT_DIRECTOR_BASE_URL,
  providerModel: DEFAULT_DIRECTOR_MODEL,
  directorProvider: DEFAULT_DIRECTOR_PROVIDER,
}
const DEFAULT_IMAGE_NAME = 'Mango Image'
const DEFAULT_IMAGE_MODEL = 'ember_drift'
const DEFAULT_IMAGE_BASE_URL = 'https://api.ai.meta.com/v1'
const CUSTOM_VIDEO_PRESET_ID: VideoByomPresetId = 'custom'
const CUSTOM_VIDEO_PRESET: VideoByomPreset = {
  id: CUSTOM_VIDEO_PRESET_ID,
  label: 'Customized',
  description: '',
  run_url: null,
  provider_model: null,
  protocol: 'custom_queue',
  capabilities: {},
  input_mode: 'provider_defined',
}

function isVideoByomPresetId(value: string): value is VideoByomPresetId {
  return value === CUSTOM_VIDEO_PRESET_ID
    || value === 'byteplus_seedance_2'
}

interface CustomVideoDraft {
  keyName: string
  baseUrl: string
  providerModel: string
  capabilities: CapabilityMap
  requestPattern: string
}

// ---------------------------------------------------------------------------
// Request pattern presets — determines how the gateway payload is structured
// ---------------------------------------------------------------------------

const REQUEST_PATTERNS: { value: string; labelKey: string; descriptionKey: string }[] = [
  {
    value: DEFAULT_VIDEO_REQUEST_PATTERN,
    labelKey: 'gatewayModal.patternBytePlusLabel',
    descriptionKey: 'gatewayModal.patternBytePlusDescription',
  },
  {
    value: 'pattern_1',
    labelKey: 'gatewayModal.patternStandardLabel',
    descriptionKey: 'gatewayModal.patternStandardDescription',
  },
  {
    value: 'pattern_2',
    labelKey: 'gatewayModal.patternReferenceLabel',
    descriptionKey: 'gatewayModal.patternReferenceDescription',
  },
]

// ---------------------------------------------------------------------------
// Example payloads — shown below the pattern selector so users can verify
// the shape matches what their model endpoint expects.
// ---------------------------------------------------------------------------

const PATTERN_EXAMPLES: Record<string, string> = {
  [DEFAULT_VIDEO_REQUEST_PATTERN]: JSON.stringify(
    {
      model: 'dreamina-seedance-2-5-260628',
      content: [
        {
          type: 'text',
          text: 'Use Image 1 as the opening frame, follow the motion from Video 1, and use Audio 1 as background music.',
        },
        {
          type: 'image_url',
          image_url: { url: 'https://ark-doc.tos-ap-southeast-1.bytepluses.com/doc_image/r2v_tea_pic1.jpg' },
          role: 'reference_image',
        },
        {
          type: 'video_url',
          video_url: { url: 'https://ark-doc.tos-ap-southeast-1.bytepluses.com/doc_video/r2v_tea_video1.mp4' },
          role: 'reference_video',
        },
        {
          type: 'audio_url',
          audio_url: { url: 'https://ark-doc.tos-ap-southeast-1.bytepluses.com/doc_audio/r2v_tea_audio1.mp3' },
          role: 'reference_audio',
        },
      ],
      generate_audio: true,
      ratio: '16:9',
      duration: 11,
      watermark: false,
    },
    null,
    2,
  ),
  pattern_1: JSON.stringify(
    {
      prompt: 'An octopus finds a football in the ocean and excitedly calls its octopus friends to come and play.',
      image_urls: ['https://storage.example.com/uploaded-image.jpg'],
      resolution: '720p',
      duration: 'auto',
      aspect_ratio: 'auto',
      generate_audio: true,
      bitrate_mode: 'standard',
    },
    null,
    2,
  ),
  pattern_2: JSON.stringify(
    {
      prompt: 'A @Image1 running through a sunlit meadow, cinematic slow motion',
      reference_image_urls: ['https://storage.example.com/uploaded-image.jpg'],
      reference_video_urls: ['https://storage.example.com/reference-video.mp4'],
      duration: 8,
      resolution: '480p',
      aspect_ratio: '16:9',
    },
    null,
    2,
  ),
}

const LIST_CAPABILITY_OPTIONS = [
  ['aspect_ratios', 'gatewayModal.capAspectRatio'],
  ['resolutions', 'gatewayModal.capResolution'],
  ['quality', 'gatewayModal.capQuality'],
  ['audio_control', 'gatewayModal.capAudioControl'],
]

function CapabilityValuesInput({
  id,
  label,
  values,
  onChange,
}: {
  id: string
  label: string
  values: string[]
  onChange: (values: string[]) => void
}) {
  return (
    <div data-testid={`gateway-modal-cap-${id}`}>
      <label htmlFor={`gateway-cap-${id}-values`} className="text-xs font-medium text-muted-foreground block mb-1">
        {label}
      </label>
      <input
        key={values.join('\u0000')}
        id={`gateway-cap-${id}-values`}
        type="text"
        defaultValue={values.join(', ')}
        onBlur={(event) => onChange(
          event.target.value.split(',').map(value => value.trim()).filter(Boolean),
        )}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
        }}
        className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        data-testid={`gateway-cap-${id}-values`}
      />
    </div>
  )
}

function maximumCapabilityValue(values: string[]): number | '' {
  const parsed = (values ?? [])
    .map(value => value.trim())
    .filter(value => /^[+-]?\d+$/.test(value))
    .map(value => Number(value))
    .filter(value => Number.isSafeInteger(value) && value >= 0)
  return parsed.length > 0 ? Math.max(...parsed) : ''
}

interface MaximumCapabilityInputProps {
  id: string
  label: string
  value: number | ''
  onChange: (rawValue: string) => void
}

function MaximumCapabilityInput({
  id,
  label,
  value,
  onChange,
}: MaximumCapabilityInputProps) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground block mb-1">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="0"
        className="h-9 w-28 rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        data-testid={`gateway-cap-${id}-maximum`}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GatewaySettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  editConfig?: GatewayConfig | null
  modelKind?: GatewayConfigKind
  directorPreset?: DirectorModelPreset
  /**
   * Context for `byom_action` (action=add_started, RFC-1577), logged
   * when this modal opens in ADD mode. Hosts that can only edit (the chat
   * action card) omit it, and so does any host with nothing to attribute.
   */
  addStartedAnalytics?: ByomAddStartedAnalytics
}

export interface ByomAddStartedAnalytics {
  entryPoint: ByomEntryPoint
  /** Saved registrations across all model kinds; null when the list failed to load. */
  configuredModelCount: number | null
}

export interface DirectorModelPreset {
  keyName: string
  baseUrl: string
  providerModel: string
  directorProvider: DirectorByomProvider
}

interface GatewayFormSnapshot {
  keyName: string
  baseUrl: string
  apiKey: string
  isDefault: boolean
  capabilities: CapabilityMap
  requestPattern: string
  modelKind: GatewayConfigKind
  directorProvider: DirectorByomProvider
  providerModel: string
  videoPresetId: VideoByomPresetId
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim()
}

/** Lossless clone: missing and explicitly empty capability fields differ. */
export function compactCapabilities(value: CapabilityMap): CapabilityMap {
  const compacted: CapabilityMap = {}
  for (const key of Object.keys(value)) {
    const entry = value[key]
    compacted[key] = Array.isArray(entry) ? [...entry] : entry
  }
  return compacted
}

function normalizeCapabilities(value: CapabilityMap): CapabilityMap {
  const normalized: CapabilityMap = {}
  const compacted = compactCapabilities(value)
  for (const key of Object.keys(compacted).sort()) {
    const entry = compacted[key]
    normalized[key] = Array.isArray(entry) ? [...entry] : entry
  }
  return normalized
}

function stringCapabilityValues(value: CapabilityMap, key: string): string[] {
  const candidate = value[key]
  if (!Array.isArray(candidate)) return []
  return candidate.filter((entry): entry is string => typeof entry === 'string')
}

function snapshotFormState({
  keyName,
  baseUrl,
  apiKey,
  isDefault,
  capabilities,
  requestPattern,
  modelKind,
  directorProvider,
  providerModel,
  videoPresetId,
}: GatewayFormSnapshot): GatewayFormSnapshot {
  return {
    keyName: normalizeText(keyName),
    baseUrl: normalizeText(baseUrl),
    apiKey: normalizeText(apiKey),
    isDefault,
    capabilities: normalizeCapabilities(capabilities),
    requestPattern,
    modelKind,
    directorProvider,
    providerModel: normalizeText(providerModel),
    videoPresetId,
  }
}

function formSnapshotsEqual(a: GatewayFormSnapshot, b: GatewayFormSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function credentialStatusClass(status: TestCredentialsResult['status']): string {
  if (status === 'auth_success') return 'text-success'
  if (status === 'auth_failed') return 'text-destructive'
  if (status === 'provider_error' || status === 'model_unavailable') return 'text-warning'
  return 'text-muted-foreground'
}

type RecordValue = Record<string, ApiValue>

function isRecord(value: ApiValue | null): value is RecordValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isCredentialStatus(value: ApiValue): value is TestCredentialsResult['status'] {
  return value === 'auth_success' ||
    value === 'auth_failed' ||
    value === 'unsupported' ||
    value === 'unavailable' ||
    value === 'provider_error' ||
    value === 'model_unavailable'
}

function isCredentialKeySource(value: ApiValue): value is TestCredentialsResult['key_source'] {
  return value === 'typed' || value === 'saved' || value === 'none'
}

function nullableString(value: ApiValue): string | null {
  return typeof value === 'string' ? value : null
}

function nullableNumber(value: ApiValue): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function credentialResultFromApiValue(value: ApiValue | null): TestCredentialsResult | null {
  const record = isRecord(value) ? value : null
  const candidate = record && isRecord(record.detail) ? record.detail : record
  if (!candidate || !isCredentialStatus(candidate.status) || !isCredentialKeySource(candidate.key_source)) {
    return null
  }

  return {
    status: candidate.status,
    supported: typeof candidate.supported === 'boolean' ? candidate.supported : true,
    provider: nullableString(candidate.provider),
    detail: nullableString(candidate.detail),
    provider_status: nullableNumber(candidate.provider_status),
    latency_ms: nullableNumber(candidate.latency_ms),
    key_source: candidate.key_source,
  }
}

function credentialStatusFromApiError(
  err: ApiRequestError,
  keySource: TestCredentialsResult['key_source'],
  fallbackProvider: string,
): TestCredentialsResult {
  const structured = credentialResultFromApiValue(err.errorData)
  if (structured) {
    return structured
  }

  return {
    status: err.status === 401 || err.status === 403 ? 'auth_failed' : 'provider_error',
    supported: true,
    provider: fallbackProvider,
    detail: err.message || err.statusText,
    provider_status: err.status,
    latency_ms: null,
    key_source: keySource,
  }
}

function addDescriptionKey(modelKind: GatewayConfigKind): string {
  if (modelKind === 'image') return 'gatewayModal.addDescriptionImage'
  if (modelKind === 'director') return 'gatewayModal.addDescriptionDirector'
  return 'gatewayModal.addDescriptionVideo'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GatewaySettingsModal({
  isOpen,
  onClose,
  onSaved,
  editConfig,
  modelKind = 'video',
  directorPreset,
  addStartedAnalytics,
}: GatewaySettingsModalProps) {
  const { t } = useTranslation('settings')
  const customProtocolUiEnabled = useCustomProtocolUiEnabled()
  const isEdit = !!editConfig
  const effectiveModelKind = editConfig?.model_kind ?? modelKind
  const isDirectorModel = effectiveModelKind === 'director'
  const isImageModel = effectiveModelKind === 'image'
  const isVideoModel = effectiveModelKind === 'video'
  const effectiveDirectorPreset = directorPreset ?? DEFAULT_DIRECTOR_PRESET
  const [keyName, setKeyName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [isDefault, setIsDefault] = useState(true)
  const [capabilities, setCapabilities] = useState<CapabilityMap>(DEFAULT_CAPABILITIES)
  const [requestPattern, setRequestPattern] = useState('pattern_1')
  const [directorProvider, setDirectorProvider] = useState<DirectorByomProvider>(DEFAULT_DIRECTOR_PROVIDER)
  const [providerModel, setProviderModel] = useState(DEFAULT_DIRECTOR_MODEL)
  const [videoPresetId, setVideoPresetId] = useState<VideoByomPresetId>(CUSTOM_VIDEO_PRESET_ID)
  const [videoPresets, setVideoPresets] = useState<VideoByomPreset[]>([CUSTOM_VIDEO_PRESET])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [urlError, setUrlError] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<TestConnectionResult | null>(null)
  const [credentialTesting, setCredentialTesting] = useState(false)
  const [credentialStatus, setCredentialStatus] = useState<TestCredentialsResult | null>(null)
  const [initialSnapshot, setInitialSnapshot] = useState<GatewayFormSnapshot | null>(null)
  const latestBaseUrlRef = useRef('')
  const inFlightTestUrlRef = useRef<string | null>(null)
  const lastTestedUrlRef = useRef<string | null>(null)
  const testRequestIdRef = useRef(0)
  const credentialRequestIdRef = useRef(0)
  const activeCredentialRequestIdRef = useRef<number | null>(null)
  const customVideoDraftRef = useRef<CustomVideoDraft>({
    keyName: '',
    baseUrl: '',
    providerModel: '',
    capabilities: DEFAULT_CAPABILITIES,
    requestPattern: DEFAULT_VIDEO_REQUEST_PATTERN,
  })

  useEffect(() => {
    latestBaseUrlRef.current = baseUrl
  }, [baseUrl])

  // byom_action / add_started: the Add-model form became visible.
  // Once per open (the reset effect below re-runs on unrelated dep changes, so
  // it is not a usable trigger). `provider` is the code's own starting value —
  // the Director wire protocol of the preset, or the video form's preset id;
  // image registrations have no provider choice. Never the endpoint or key.
  const addStartedLoggedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      addStartedLoggedRef.current = false
      return
    }
    if (isEdit || addStartedAnalytics === undefined || addStartedLoggedRef.current) return
    addStartedLoggedRef.current = true
    const provider = isDirectorModel
      ? effectiveDirectorPreset.directorProvider
      : isVideoModel
        ? CUSTOM_VIDEO_PRESET_ID
        : undefined
    const count = addStartedAnalytics.configuredModelCount
    track('byom_action', {
      action: 'add_started',
      model_type: effectiveModelKind,
      entry_point: addStartedAnalytics.entryPoint,
      ...(provider !== undefined ? { provider } : {}),
      ...(count !== null ? { configured_model_count_bucket: countBucket(count) } : {}),
    })
  }, [
    isOpen,
    isEdit,
    addStartedAnalytics,
    isDirectorModel,
    isVideoModel,
    effectiveDirectorPreset.directorProvider,
    effectiveModelKind,
  ])

  useEffect(() => {
    if (!isOpen || !isVideoModel) return
    let cancelled = false
    void getVideoByomPresets()
      .then((presets) => {
        if (!cancelled && presets.length > 0) {
          setVideoPresets(presets.filter((preset) => preset.id !== 'byteplus_seedance_2'))
        }
      })
      .catch(() => {
        log({
          event: 'preset:catalog-error',
          trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
          error: 'Failed to load video preset catalog', // i18n-exempt: internal NAD diagnostic, not rendered
        })
      })
    return () => { cancelled = true }
  }, [editConfig?.id, isOpen, isVideoModel])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      log({
        event: 'modal:open',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
        mode: isEdit ? 'edit' : 'add',
        config_id: editConfig?.id ?? null,
        has_existing_key: isEdit && !!editConfig?.api_key_masked,
        protocol_configured: !!editConfig?.protocol_config,
        needs_api_key: !!editConfig?.needs_api_key,
        custom_protocol_ui_enabled: customProtocolUiEnabled,
      })
      if (editConfig) {
        const nextKeyName = editConfig.key_name ?? ''
        const nextBaseUrl = editConfig.base_url ?? ''
        const nextApiKey = ''
        const nextIsDefault = editConfig.is_default ?? false
        const nextDirectorProvider = editConfig.director_provider ?? DEFAULT_DIRECTOR_PROVIDER
        const nextProviderModel = editConfig.provider_model
          ?? (isImageModel ? DEFAULT_IMAGE_MODEL : isVideoModel ? '' : DEFAULT_DIRECTOR_MODEL)
        const nextVideoPresetId = editConfig.video_preset_id === 'byteplus_seedance_2'
          ? CUSTOM_VIDEO_PRESET_ID
          : editConfig.video_preset_id ?? CUSTOM_VIDEO_PRESET_ID
        const caps = editConfig.capabilities
        let nextCapabilities: CapabilityMap
        if (caps) {
          // The persisted JSON is the source of truth. Keep unknown and future
          // fields byte-for-byte equivalent through unrelated Settings edits.
          nextCapabilities = compactCapabilities(caps)
        } else {
          nextCapabilities = DEFAULT_CAPABILITIES
        }
        const editPattern = editConfig.capabilities?.request_pattern
        const nextRequestPattern = typeof editPattern === 'string' ? editPattern : 'pattern_1'
        setKeyName(nextKeyName)
        setBaseUrl(nextBaseUrl)
        setApiKey(nextApiKey) // Don't pre-fill the masked key
        setIsDefault(nextIsDefault)
        setCapabilities(nextCapabilities)
        setRequestPattern(nextRequestPattern)
        setDirectorProvider(nextDirectorProvider)
        setProviderModel(nextProviderModel)
        setVideoPresetId(nextVideoPresetId)
        customVideoDraftRef.current = {
          keyName: nextKeyName,
          baseUrl: nextVideoPresetId === CUSTOM_VIDEO_PRESET_ID ? nextBaseUrl : '',
          providerModel: nextVideoPresetId === CUSTOM_VIDEO_PRESET_ID ? nextProviderModel : '',
          capabilities: nextVideoPresetId === CUSTOM_VIDEO_PRESET_ID
            ? nextCapabilities
            : DEFAULT_CAPABILITIES,
          requestPattern: nextVideoPresetId === CUSTOM_VIDEO_PRESET_ID
            ? nextRequestPattern
            : 'pattern_1',
        }
        setInitialSnapshot(snapshotFormState({
          keyName: nextKeyName,
          baseUrl: nextBaseUrl,
          apiKey: nextApiKey,
          isDefault: nextIsDefault,
          capabilities: nextCapabilities,
          requestPattern: nextRequestPattern,
          modelKind: effectiveModelKind,
          directorProvider: nextDirectorProvider,
          providerModel: nextProviderModel,
          videoPresetId: nextVideoPresetId,
        }))
      } else {
        const nextCapabilities = DEFAULT_CAPABILITIES
        const nextKeyName = isDirectorModel
          ? effectiveDirectorPreset.keyName
          : isImageModel
            ? DEFAULT_IMAGE_NAME
            : DEFAULT_VIDEO_MODEL_NAME
        const nextBaseUrl = isDirectorModel
          ? effectiveDirectorPreset.baseUrl
          : isImageModel
            ? DEFAULT_IMAGE_BASE_URL
            : BYTEPLUS_RUN_URL_EXAMPLE
        const nextIsDefault = !isDirectorModel
        const nextProviderModel = isDirectorModel
          ? effectiveDirectorPreset.providerModel
          : isImageModel
            ? DEFAULT_IMAGE_MODEL
            : ''
        const nextDirectorProvider = isDirectorModel
          ? effectiveDirectorPreset.directorProvider
          : DEFAULT_DIRECTOR_PROVIDER
        const nextVideoPresetId = CUSTOM_VIDEO_PRESET_ID
        setKeyName(nextKeyName)
        setBaseUrl(nextBaseUrl)
        setApiKey('')
        setIsDefault(nextIsDefault)
        setCapabilities(nextCapabilities)
        setRequestPattern(DEFAULT_VIDEO_REQUEST_PATTERN)
        setDirectorProvider(nextDirectorProvider)
        setProviderModel(nextProviderModel)
        setVideoPresetId(nextVideoPresetId)
        customVideoDraftRef.current = {
          keyName: nextKeyName,
          baseUrl: nextBaseUrl,
          providerModel: nextProviderModel,
          capabilities: nextCapabilities,
          requestPattern: DEFAULT_VIDEO_REQUEST_PATTERN,
        }
        setInitialSnapshot(snapshotFormState({
          keyName: nextKeyName,
          baseUrl: nextBaseUrl,
          apiKey: '',
          isDefault: nextIsDefault,
          capabilities: nextCapabilities,
          requestPattern: DEFAULT_VIDEO_REQUEST_PATTERN,
          modelKind: effectiveModelKind,
          directorProvider: nextDirectorProvider,
          providerModel: nextProviderModel,
          videoPresetId: nextVideoPresetId,
        }))
      }
      setError(null)
      setUrlError(null)
      setConnectionStatus(null)
      setCredentialStatus(null)
      setCredentialTesting(false)
      inFlightTestUrlRef.current = null
      lastTestedUrlRef.current = null
      testRequestIdRef.current += 1
      credentialRequestIdRef.current += 1
      activeCredentialRequestIdRef.current = null
    }
    // `isEdit` is derived from `editConfig` and therefore changes
    // in lockstep with it — declared in deps for the exhaustive-deps lint
    // rule, no runtime behaviour change.
  }, [
    isOpen,
    editConfig,
    isEdit,
    effectiveModelKind,
    isDirectorModel,
    isImageModel,
    isVideoModel,
    effectiveDirectorPreset,
    customProtocolUiEnabled,
  ])

  const applyCapabilities = useCallback((next: CapabilityMap) => setCapabilities(next), [])

  const handleCapabilityValuesChange = useCallback((key: string, next: string[]) => {
    applyCapabilities({ ...capabilities, [key]: next })
    log({
      event: 'capability:values-change',
      trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
      capability: key,
      value_count: next.length,
    })
  }, [applyCapabilities, capabilities, editConfig?.id])

  const handleReferenceMaximumChange = useCallback((
    key: 'reference_images_support' | 'reference_videos_support' | 'reference_audios_support' | 'max_total_references',
    rawValue: string,
  ) => {
    if (rawValue === '') {
      const rest = { ...capabilities }
      delete rest[key]
      applyCapabilities(rest)
      return
    }
    const parsed = Number(rawValue)
    if (!Number.isSafeInteger(parsed) || parsed < 0) return
    applyCapabilities({ ...capabilities, [key]: [String(parsed)] })
    log({
      event: 'capability:reference-maximum',
      trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
      capability: key,
      maximum: parsed,
    })
  }, [applyCapabilities, capabilities, editConfig?.id])

  const selectedVideoPreset = useMemo(
    () => videoPresets.find((preset) => preset.id === videoPresetId) ?? CUSTOM_VIDEO_PRESET,
    [videoPresetId, videoPresets],
  )
  const isKnownVideoPreset = isVideoModel && videoPresetId !== CUSTOM_VIDEO_PRESET_ID

  const videoPresetLabel = useCallback((preset: VideoByomPreset) => {
    if (preset.id === CUSTOM_VIDEO_PRESET_ID) return t('gatewayModal.customVideoPresetLabel')
    return preset.label
  }, [t])

  const videoPresetDescription = useCallback((preset: VideoByomPreset) => {
    if (preset.id === CUSTOM_VIDEO_PRESET_ID) {
      return t('gatewayModal.customVideoPresetDescription')
    }
    return preset.description
  }, [t])

  const handleVideoPresetChange = useCallback((nextId: VideoByomPresetId) => {
    const nextPreset = videoPresets.find((preset) => preset.id === nextId)
    if (!nextPreset) return
    if (videoPresetId === CUSTOM_VIDEO_PRESET_ID && nextId !== CUSTOM_VIDEO_PRESET_ID) {
      customVideoDraftRef.current = {
        keyName,
        baseUrl,
        providerModel,
        capabilities,
        requestPattern,
      }
    }
    setVideoPresetId(nextId)
    if (nextId === CUSTOM_VIDEO_PRESET_ID) {
      const customDraft = customVideoDraftRef.current
      setKeyName(customDraft.keyName)
      setBaseUrl(customDraft.baseUrl)
      latestBaseUrlRef.current = customDraft.baseUrl
      setProviderModel(customDraft.providerModel)
      setCapabilities(customDraft.capabilities)
      setRequestPattern(customDraft.requestPattern)
    } else {
      setBaseUrl(nextPreset.run_url ?? '')
      latestBaseUrlRef.current = nextPreset.run_url ?? ''
      setProviderModel(nextPreset.provider_model ?? '')
      setCapabilities(nextPreset.capabilities)
      setRequestPattern('pattern_1')
      if (!keyName.trim() || keyName === DEFAULT_VIDEO_MODEL_NAME) {
        setKeyName(videoPresetLabel(nextPreset))
      }
    }
    setConnectionStatus(null)
    setCredentialStatus(null)
    setUrlError(null)
    credentialRequestIdRef.current += 1
    log({
      event: 'preset:select',
      trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
      preset_id: nextId,
    })
  }, [
    baseUrl,
    capabilities,
    editConfig?.id,
    keyName,
    providerModel,
    requestPattern,
    videoPresetId,
    videoPresetLabel,
    videoPresets,
  ])

  const currentSnapshot = useMemo(
    () =>
      snapshotFormState({
        keyName,
        baseUrl,
        apiKey,
        isDefault,
        capabilities,
        requestPattern,
        modelKind: effectiveModelKind,
        directorProvider,
        providerModel,
        videoPresetId,
      }),
    [
      keyName,
      baseUrl,
      apiKey,
      isDefault,
      capabilities,
      requestPattern,
      effectiveModelKind,
      directorProvider,
      providerModel,
      videoPresetId,
    ],
  )

  const isDirty = useMemo(
    () => (initialSnapshot ? !formSnapshotsEqual(initialSnapshot, currentSnapshot) : false),
    [initialSnapshot, currentSnapshot],
  )

  // Masked display of the existing key, shown read-only in edit mode. The raw
  // key is never returned by the backend (CWE-312 show-once) — only the mask.
  // Prod vault refs mask to the sentinel "vault-secured"; local refs mask to
  // "abc...wxyz". Either way we surface a clearly-populated status so the field
  // doesn't read as blank/broken (the QA-reported symptom).
  const currentKeyDisplay = useMemo(() => {
    if (customProtocolUiEnabled && editConfig?.needs_api_key) {
      return t('gatewaySection.needsApiKey')
    }
    const masked = (editConfig?.api_key_masked ?? '').trim()
    if (!masked || masked === 'vault-secured') return '•••••••••• (saved securely)'
    return masked
  }, [customProtocolUiEnabled, editConfig, t])

  const customVideoUrlMissing = isVideoModel && !isKnownVideoPreset && !baseUrl.trim()
  const videoPresetChanged = isVideoModel && isEdit && (
    (editConfig?.video_preset_id ?? CUSTOM_VIDEO_PRESET_ID) !== videoPresetId
  )
  const changedPresetKeyMissing = videoPresetChanged && !apiKey.trim()
  const saveDisabled = saving || !!urlError || customVideoUrlMissing || changedPresetKeyMissing
    || (isEdit ? !isDirty : !apiKey.trim())
  const currentUrlIsValid = !validateUrl(baseUrl) && !!baseUrl.trim()
  // Image BYOM endpoints only document generation/edit calls; do not burn user quota
  // with a real image request just to test a key.
  const supportsCredentialTest = currentUrlIsValid && !isKnownVideoPreset && (
    isDirectorModel
      ? supportsDirectorKeyTest(baseUrl, directorProvider)
      : isFalCredentialsUrl(baseUrl)
  )
  const credentialButtonDisabled =
    credentialTesting || !supportsCredentialTest || changedPresetKeyMissing
    || (!isEdit && !apiKey.trim())

  // byom_action click events (test / save). Emitted only when the opening
  // surface told us its entry point; it is never guessed.
  const trackByomClick = (action: 'test_clicked' | 'save_clicked') => {
    const entryPoint = addStartedAnalytics?.entryPoint
    if (entryPoint === undefined) return
    track('byom_action', { action, model_type: effectiveModelKind, entry_point: entryPoint })
  }

  const handleSave = useCallback(async () => {
    if (isEdit && !isDirty) {
      return
    }
    if (!isEdit && !apiKey.trim()) {
      setError(t('gatewayModal.apiKeyRequired'))
      return
    }
    if ((isDirectorModel || isImageModel) && !providerModel.trim()) {
      setError(t('gatewayModal.providerModelRequired'))
      return
    }
    if (customVideoUrlMissing) {
      setUrlError('gatewayModal.urlRequired')
      return
    }
    // Validate URL before saving
    const urlValidation = validateUrl(baseUrl)
    if (urlValidation) {
      setUrlError(urlValidation)
      log({
        event: 'url:validate-reject',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
        error: urlValidation,
      })
      return
    }
    setSaving(true)
    setError(null)
    try {
      // Merge the selected request_pattern as a plain string into capabilities JSONB
      const capsWithPattern = { ...compactCapabilities(capabilities), request_pattern: requestPattern }
      const savedCapabilities = isDirectorModel || isImageModel || isKnownVideoPreset
        ? {}
        : capsWithPattern
      // Default selection is radio-like: new video/image models become the
      // default, and an existing default cannot be unchecked in the modal.
      const savedIsDefault = isDirectorModel ? false : isEdit ? isDefault : true
      const shouldSaveProviderModel = isDirectorModel || isImageModel || isVideoModel
      const shouldSaveVideoPreset = isVideoModel && (
        isKnownVideoPreset || (
          editConfig?.video_preset_id != null
          && editConfig.video_preset_id !== CUSTOM_VIDEO_PRESET_ID
        )
      )
      if (isEdit && editConfig) {
        await updateGatewayConfig(editConfig.id, {
          api_key: apiKey.trim() || undefined,
          key_name: keyName.trim() || undefined,
          base_url: isKnownVideoPreset ? undefined : baseUrl.trim() || undefined,
          capabilities: savedCapabilities,
          is_default: savedIsDefault,
          model_kind: effectiveModelKind,
          director_provider: isDirectorModel ? directorProvider : undefined,
          provider_model: shouldSaveProviderModel && providerModel.trim()
            ? providerModel.trim()
            : undefined,
          ...(shouldSaveVideoPreset ? { video_preset_id: videoPresetId } : {}),
        })
        log({
          event: 'modal:save',
          trace_id: `gateway-settings:${editConfig.id}:modal`,
          mode: 'edit',
          config_id: editConfig.id,
          model_kind: effectiveModelKind,
          is_default: savedIsDefault,
          request_pattern: requestPattern,
          provider_model_set: !!providerModel.trim(),
          capability_keys: Object.keys(savedCapabilities).sort(),
          duration_value_count: stringCapabilityValues(savedCapabilities, 'duration_range').length,
          // true = user typed a replacement key; false = blank ⇒ keep current key
          key_changed: !!apiKey.trim(),
        })
      } else {
        await createGatewayConfig({
          api_key: apiKey.trim(),
          key_name: keyName.trim() || undefined,
          base_url: isKnownVideoPreset ? undefined : baseUrl.trim() || undefined,
          capabilities: savedCapabilities,
          is_default: savedIsDefault,
          model_kind: effectiveModelKind,
          director_provider: isDirectorModel ? directorProvider : undefined,
          provider_model: shouldSaveProviderModel && providerModel.trim()
            ? providerModel.trim()
            : undefined,
          ...(shouldSaveVideoPreset ? { video_preset_id: videoPresetId } : {}),
        })
        log({
          event: 'modal:save',
          trace_id: 'gateway-settings:new:modal',
          mode: 'add',
          model_kind: effectiveModelKind,
          is_default: savedIsDefault,
          request_pattern: requestPattern,
          provider_model_set: !!providerModel.trim(),
          capability_keys: Object.keys(savedCapabilities).sort(),
          duration_value_count: stringCapabilityValues(savedCapabilities, 'duration_range').length,
        })
      }
      onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save configuration'
      log({
        event: 'modal:save-error',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
        mode: isEdit ? 'edit' : 'add',
        error: msg,
      })
      setError(msg)
    } finally {
      setSaving(false)
    }
  }, [
    isEdit,
    isDirty,
    editConfig,
    apiKey,
    keyName,
    baseUrl,
    capabilities,
    requestPattern,
    isDefault,
    onSaved,
    t,
    isDirectorModel,
    isImageModel,
    providerModel,
    effectiveModelKind,
    directorProvider,
    isKnownVideoPreset,
    isVideoModel,
    videoPresetId,
    customVideoUrlMissing,
  ])

  const handleTestConnection = useCallback(async (trigger: 'button' | 'blur' | 'enter' = 'button') => {
    const url = baseUrl.trim()
    const urlValidation = validateUrl(url)
    if (urlValidation) {
      setUrlError(urlValidation)
      log({
        event: 'url:validate-reject',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`,
        trigger,
        error: urlValidation,
      })
      return
    }
    if (!url) {
      setConnectionStatus(null)
      return
    }
    if (isDirectorModel || isImageModel || isKnownVideoPreset) {
      setConnectionStatus({
        status: 'reachable',
        detail: t('gatewayModal.urlAccepted'),
      })
      lastTestedUrlRef.current = url
      log({
        event: isKnownVideoPreset
          ? 'url:validate-video-preset'
          : isImageModel ? 'url:validate-image' : 'url:validate-director',
        trace_id: 'gateway-settings:test-connection',
        trigger,
      })
      return
    }
    if (inFlightTestUrlRef.current === url) {
      return
    }
    if (trigger !== 'button' && lastTestedUrlRef.current === url && connectionStatus !== null) {
      return
    }
    const requestId = testRequestIdRef.current + 1
    testRequestIdRef.current = requestId
    inFlightTestUrlRef.current = url
    setTesting(true)
    setConnectionStatus(null)
    try {
      const result = await testGatewayConnection(url)
      if (testRequestIdRef.current === requestId && latestBaseUrlRef.current.trim() === url) {
        setConnectionStatus(result)
        lastTestedUrlRef.current = url
      }
      log({
        event: 'url:test-connection',
        trace_id: 'gateway-settings:test-connection',
        trigger,
        status: result.status,
        latency_ms: result.latency_ms,
      })
    } catch {
      if (testRequestIdRef.current === requestId && latestBaseUrlRef.current.trim() === url) {
        setConnectionStatus({ status: 'unreachable', detail: 'Failed to test connection' })
        lastTestedUrlRef.current = url
      }
    } finally {
      if (inFlightTestUrlRef.current === url) {
        inFlightTestUrlRef.current = null
      }
      if (testRequestIdRef.current === requestId) {
        setTesting(false)
      }
    }
  }, [baseUrl, connectionStatus, editConfig?.id, isDirectorModel, isImageModel, isKnownVideoPreset, t])

  const handleTestCredentials = useCallback(async () => {
    const url = baseUrl.trim()
    const credentialProvider = isDirectorModel
      ? directorProvider === 'openai_chat_completions' ? 'OpenAI' : 'Muse Spark'
      : isImageModel ? 'OpenAI-compatible image' : selectedVideoPreset.label
    const urlValidation = validateUrl(url)
    if (urlValidation) {
      setUrlError(urlValidation)
      setCredentialStatus(null)
      log({
        event: 'credentials:test-error',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:credentials`,
        reason: 'url-validation',
        error: urlValidation,
      })
      return
    }
    if (!supportsCredentialTest) {
      const unsupported: TestCredentialsResult = {
        status: 'unsupported',
        supported: false,
        provider: credentialProvider,
        detail: t(isDirectorModel || isImageModel ? 'gatewayModal.keyUnsupported' : 'gatewayModal.credentialsUnsupported'),
        latency_ms: null,
        provider_status: null,
        key_source: 'none',
      }
      setCredentialStatus(unsupported)
      log({
        event: 'credentials:test-result',
        trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:credentials`,
        status: 'unsupported',
      })
      return
    }
    if (!isEdit && !apiKey.trim()) {
      const unavailable: TestCredentialsResult = {
        status: 'unavailable',
        supported: true,
        provider: credentialProvider,
        detail: t('gatewayModal.credentialsEnterKey'),
        latency_ms: null,
        provider_status: null,
        key_source: 'none',
      }
      setCredentialStatus(unavailable)
      return
    }

    const requestId = credentialRequestIdRef.current + 1
    credentialRequestIdRef.current = requestId
    const traceId = `gateway-settings:${editConfig?.id ?? 'new'}:credentials`
    const keySource = apiKey.trim() ? 'typed' : isEdit ? 'saved' : 'none'
    activeCredentialRequestIdRef.current = requestId
    setCredentialTesting(true)
    setCredentialStatus(null)
    log({
      event: 'credentials:test-start',
      trace_id: traceId,
      mode: isEdit ? 'edit' : 'add',
      config_id: editConfig?.id ?? null,
      key_source: keySource,
    })
    try {
      const result = await testGatewayCredentials({
        url,
        api_key: apiKey.trim() || undefined,
        ...(isEdit && editConfig?.id ? { config_id: editConfig.id } : {}),
        trace_id: traceId,
        ...(isDirectorModel
          ? {
              model_kind: 'director',
              director_provider: directorProvider,
              provider_model: providerModel.trim(),
            }
          : {}),
        ...(isVideoModel ? { video_preset_id: videoPresetId } : {}),
      })
      if (credentialRequestIdRef.current === requestId && latestBaseUrlRef.current.trim() === url) {
        setCredentialStatus(result)
      }
      log({
        event: 'credentials:test-result',
        trace_id: traceId,
        status: result.status,
        provider: result.provider ?? null,
        provider_status: result.provider_status ?? null,
        key_source: result.key_source,
        latency_ms: result.latency_ms ?? null,
      })
    } catch (err) {
      if (err instanceof ApiRequestError) {
        const result = credentialStatusFromApiError(err, keySource, credentialProvider)
        if (credentialRequestIdRef.current === requestId && latestBaseUrlRef.current.trim() === url) {
          setCredentialStatus(result)
        }
        log({
          event: 'credentials:test-error',
          trace_id: traceId,
          mode: isEdit ? 'edit' : 'add',
          status: result.status,
          error: result.detail ?? err.message,
          api_status: err.status,
          provider_status: result.provider_status ?? null,
          key_source: result.key_source,
        })
        return
      }

      const msg = err instanceof Error ? err.message : t('gatewayModal.credentialsFailed')
      if (credentialRequestIdRef.current === requestId && latestBaseUrlRef.current.trim() === url) {
        setCredentialStatus({
          status: 'unavailable',
          supported: true,
          provider: credentialProvider,
          detail: msg,
          latency_ms: null,
          provider_status: null,
          key_source: keySource,
        })
      }
      log({
        event: 'credentials:test-error',
        trace_id: traceId,
        mode: isEdit ? 'edit' : 'add',
        error: msg,
      })
    } finally {
      if (activeCredentialRequestIdRef.current === requestId) {
        activeCredentialRequestIdRef.current = null
        setCredentialTesting(false)
      }
    }
  }, [
    apiKey,
    baseUrl,
    editConfig?.id,
    isEdit,
    t,
    supportsCredentialTest,
    isDirectorModel,
    isImageModel,
    isVideoModel,
    directorProvider,
    providerModel,
    selectedVideoPreset.label,
    videoPresetId,
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t(isEdit ? 'gatewayModal.editModel' : 'gatewayModal.addModel')} size="md">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <p className="text-xs font-medium text-muted-foreground">
          {t(isEdit ? 'gatewayModal.editDescription' : addDescriptionKey(effectiveModelKind))}
        </p>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
            {error}
          </div>
        )}

        {isVideoModel && (
          <div>
            <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">
              {t('gatewayModal.videoProvider')}
            </label>
            <select
              value={videoPresetId}
              onChange={(event) => {
                if (isVideoByomPresetId(event.target.value)) {
                  handleVideoPresetChange(event.target.value)
                }
              }}
              className="w-full h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground hover:bg-surface-3 hover:border-input-border-hover focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="gateway-modal-video-preset"
            >
              {videoPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>{videoPresetLabel(preset)}</option>
              ))}
            </select>
            <p className="text-xxs text-muted-foreground mt-1" data-testid="gateway-modal-video-preset-description">
              {videoPresetDescription(selectedVideoPreset)}
            </p>
          </div>
        )}

        <div>
          <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">{t('gatewayModal.modelName')}</label>
          <input
            type="text"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder={t('gatewayModal.modelNamePlaceholder')}
            className="w-full rounded-md border border-border bg-surface-2 px-3 py-1.5 text-xs font-medium text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none"
            data-testid="gateway-modal-key-name"
          />
        </div>

        <div>
          <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">{t('gatewayModal.runUrl')}</label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={baseUrl}
              readOnly={isKnownVideoPreset}
              onChange={(e) => {
                latestBaseUrlRef.current = e.target.value
                setBaseUrl(e.target.value)
                setUrlError(null)
                setConnectionStatus(null)
                setCredentialStatus(null)
                credentialRequestIdRef.current += 1
              }}
              onFocus={(e) => {
                if (!isEdit && !isKnownVideoPreset && baseUrl === BYTEPLUS_RUN_URL_EXAMPLE) {
                  e.currentTarget.select()
                }
              }}
              onBlur={() => { void handleTestConnection('blur') }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleTestConnection('enter')
                }
              }}
              placeholder={t('gatewayModal.runUrlPlaceholder')}
              className={`flex-1 rounded-md border ${urlError ? 'border-destructive' : 'border-input-border'} bg-input-bg px-3 py-1.5 text-xs font-medium text-foreground font-mono placeholder:text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${isKnownVideoPreset ? 'cursor-not-allowed text-muted-foreground' : ''}`}
              data-testid="gateway-modal-base-url"
            />
            <button
              type="button"
              onClick={() => { trackByomClick('test_clicked'); void handleTestConnection('button') }}
              disabled={!!urlError || !baseUrl.trim() || testing || isKnownVideoPreset}
              className="shrink-0 px-3 py-1.5 rounded-md border border-border bg-surface-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 cursor-pointer"
              data-testid="gateway-modal-test-connection"
            >
              {t(testing ? 'gatewayModal.testing' : (isDirectorModel || isImageModel) ? 'gatewayModal.validateUrl' : 'gatewayModal.testConnection')}
            </button>
          </div>
          {urlError ? (
            <p className="text-xxs text-destructive mt-0.5" data-testid="gateway-modal-url-error">{t(urlError)}</p>
          ) : connectionStatus ? (
            <p className={`text-xxs mt-0.5 ${
              connectionStatus.status === 'reachable' ? 'text-green-500' :
              connectionStatus.status === 'auth_failed' ? 'text-yellow-500' :
              'text-destructive'
            }`} data-testid="gateway-modal-connection-status">
              {connectionStatus.status === 'reachable'
                ? `${connectionStatus.detail ?? t('gatewayModal.connectionSuccessful')}${connectionStatus.latency_ms != null ? ` (${connectionStatus.latency_ms}ms)` : ''}`
                : connectionStatus.status === 'auth_failed'
                  ? t('gatewayModal.connectionRequiresAuth')
                  : t('gatewayModal.unreachable', { detail: connectionStatus.detail ?? t('gatewayModal.connectionFailed') })}
            </p>
          ) : (
            <p className="text-xxs text-muted-foreground mt-0.5">
              {t(isKnownVideoPreset ? 'gatewayModal.presetManagedUrlHelp' : 'gatewayModal.runUrlHelp')}
            </p>
          )}
        </div>

        {(isDirectorModel || isImageModel || isVideoModel) && (
          <div>
            <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">
              {t('gatewayModal.providerModel')}
            </label>
            <input
              type="text"
              value={providerModel}
              onChange={(e) => setProviderModel(e.target.value)}
              readOnly={isKnownVideoPreset}
              placeholder={isVideoModel ? t('gatewayModal.providerModelPlaceholderVideo') : isImageModel ? DEFAULT_IMAGE_MODEL : DEFAULT_DIRECTOR_MODEL}
              className={`w-full rounded-md border border-input-border bg-input-bg px-3 py-1.5 text-xs font-medium text-foreground font-mono placeholder:text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover focus:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${isKnownVideoPreset ? 'cursor-not-allowed text-muted-foreground' : ''}`}
              data-testid="gateway-modal-provider-model"
            />
            {isImageModel && (
              <p className="text-xxs text-muted-foreground mt-0.5" data-testid="gateway-modal-provider-model-help">
                {t('gatewayModal.providerModelHelpImage')}
              </p>
            )}
          </div>
        )}

        <div>
          {isEdit && (
            <div className="mb-2">
              <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">{t('gatewayModal.currentAppKey')}</label>
              <input
                type="text"
                value={currentKeyDisplay}
                readOnly
                disabled
                className="w-full rounded-md border border-border bg-surface-1 px-3 py-1.5 text-xs font-medium text-muted-foreground font-mono cursor-not-allowed"
                data-testid="gateway-modal-current-key"
              />
            </div>
          )}
          <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">
            {t(isEdit ? 'gatewayModal.newAppKey' : 'gatewayModal.apiKeyLabel')}
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="password"
              value={apiKey}
              autoFocus={customProtocolUiEnabled && editConfig?.needs_api_key === true}
              onChange={(e) => {
                setApiKey(e.target.value)
                setCredentialStatus(null)
                credentialRequestIdRef.current += 1
              }}
              placeholder={t(isEdit ? 'gatewayModal.keepCurrentKeyPlaceholder' : 'gatewayModal.enterApiKeyPlaceholder')}
              className="flex-1 rounded-md border border-input-border bg-input-bg px-3 py-1.5 text-xs font-medium text-foreground placeholder:text-muted-foreground hover:bg-surface-3 hover:border-input-border-hover focus:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              data-testid="gateway-modal-api-key"
            />
            {(isVideoModel || (isDirectorModel && supportsCredentialTest)) && (
              <button
                type="button"
                onClick={() => { trackByomClick('test_clicked'); void handleTestCredentials() }}
                disabled={credentialButtonDisabled}
                className="shrink-0 px-3 py-1.5 rounded-md border border-border bg-surface-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                data-testid="gateway-modal-test-credentials"
              >
                {t(credentialTesting
                  ? isDirectorModel ? 'gatewayModal.testingKey' : 'gatewayModal.testingCredentials'
                  : isDirectorModel ? 'gatewayModal.testKey' : 'gatewayModal.testCredentials')}
              </button>
            )}
          </div>
          {changedPresetKeyMissing ? (
            <p className="text-xxs text-warning mt-0.5" data-testid="gateway-modal-preset-key-required">
              {t('gatewayModal.presetChangeKeyRequired')}
            </p>
          ) : credentialStatus ? (
            <p
              className={`text-xxs mt-0.5 ${credentialStatusClass(credentialStatus.status)}`}
              data-testid="gateway-modal-credential-status"
            >
              {credentialStatus.status === 'auth_success'
                ? credentialStatus.detail ?? t('gatewayModal.credentialsAccepted')
                : credentialStatus.status === 'auth_failed'
                  ? t('gatewayModal.credentialsRejected', { detail: credentialStatus.detail ?? t('gatewayModal.credentialsFailed') })
                  : credentialStatus.status === 'unsupported'
                    ? credentialStatus.detail ?? t(isDirectorModel || isImageModel ? 'gatewayModal.keyUnsupported' : 'gatewayModal.credentialsUnsupported')
                    : credentialStatus.status === 'provider_error'
                      ? t('gatewayModal.credentialsProviderError', { detail: credentialStatus.detail ?? t('gatewayModal.credentialsFailed') })
                      : credentialStatus.detail ?? t('gatewayModal.credentialsUnavailable')}
            </p>
          ) : currentUrlIsValid && !supportsCredentialTest && !isImageModel ? (
            <p className="text-xxs text-muted-foreground mt-0.5" data-testid="gateway-modal-credential-unsupported">
              {t(isDirectorModel ? 'gatewayModal.keyUnsupported' : 'gatewayModal.credentialsUnsupported')}
            </p>
          ) : (
            <p className="text-xxs text-muted-foreground mt-0.5">
              {t(isDirectorModel || isImageModel
                ? isEdit ? 'gatewayModal.keyHelpEdit' : 'gatewayModal.keyHelpAdd'
                : isEdit ? 'gatewayModal.credentialsHelpEdit' : 'gatewayModal.credentialsHelpAdd')}
            </p>
          )}
          {isEdit && (
            <p className="text-xxs text-muted-foreground mt-0.5">
              {t('gatewayModal.keepCurrentKeyHelp')}
            </p>
          )}
        </div>

        {isVideoModel && !isKnownVideoPreset && customProtocolUiEnabled && editConfig?.protocol_config && (
          <div data-testid="gateway-modal-custom-protocol">
            <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">
              {t('gatewayModal.customProtocol')}
            </label>
            <p className="text-xxs text-muted-foreground mb-2">
              {t('gatewayModal.customProtocolDescription')}
            </p>
            <pre className="rounded-lg border border-input-border bg-input-bg px-3 py-2 text-xxs text-muted-foreground font-mono overflow-x-auto max-h-64 leading-relaxed whitespace-pre-wrap">
              {JSON.stringify(editConfig.protocol_config, null, 2)}
            </pre>
          </div>
        )}

        {isVideoModel && !isKnownVideoPreset && (!customProtocolUiEnabled || !editConfig?.protocol_config) && (
          <div>
            <label className="text-xxs uppercase tracking-wider text-muted-foreground block mb-1">{t('gatewayModal.requestPattern')}</label>
            <p className="text-xxs text-muted-foreground mb-2">
              {t('gatewayModal.requestPatternDescription')}
            </p>
            <div className="space-y-1.5" data-testid="gateway-modal-request-pattern">
              {REQUEST_PATTERNS.map(({ value, labelKey, descriptionKey }) => (
                <label
                  key={value}
                  className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                    requestPattern === value
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-border bg-surface-2 hover:border-border/80'
                  }`}
                  data-testid={`gateway-pattern-${value}`}
                >
                  <input
                    type="radio"
                    name="request_pattern"
                    value={value}
                    checked={requestPattern === value}
                    onChange={() => { setRequestPattern(value); log({ event: 'pattern:select', trace_id: `gateway-settings:${editConfig?.id ?? 'new'}:modal`, pattern: value }) }}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs text-foreground font-medium">{t(labelKey)}</span>
                    <p className="text-xxs text-muted-foreground mt-0.5">{t(descriptionKey)}</p>
                  </div>
                </label>
              ))}
            </div>
            {/* Example payload preview for the selected pattern */}
            <div className="mt-2" data-testid="gateway-modal-pattern-example">
              <p className="text-xxs text-muted-foreground mb-1">{t('gatewayModal.examplePayload')}</p>
              <pre className="rounded-md border border-border bg-black/30 px-3 py-2 text-xxs text-muted-foreground font-mono overflow-x-auto max-h-48 leading-relaxed">
                {PATTERN_EXAMPLES[requestPattern]}
              </pre>
            </div>
          </div>
        )}

        {!isDirectorModel && (
          <label className="flex items-center gap-2 cursor-pointer" data-testid="gateway-modal-is-default">
            <input
              type="checkbox"
              checked={isDefault}
              disabled={isDefault || editConfig?.enabled === false}
              onChange={() => setIsDefault(true)}
              className="rounded border-border"
            />
            <span className="text-xs font-medium text-foreground">
              {t(isImageModel ? 'gatewayModal.makeDefaultImage' : 'gatewayModal.makeDefault')}
            </span>
            <span className="text-xxs text-muted-foreground">
              {t(isImageModel ? 'gatewayModal.makeDefaultImageHint' : 'gatewayModal.makeDefaultHint')}
            </span>
          </label>
        )}

        {isVideoModel && !isKnownVideoPreset && (
          <div className="border-t border-border pt-3">
            <p className="text-xxs uppercase tracking-wider text-muted-foreground mb-3">
              {t('gatewayModal.capabilities')}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  {t('gatewayModal.capDuration')}
                </label>
                <p className="text-xxs text-text-muted mb-2" id="gateway-duration-hint">
                  {t('gatewayModal.capDurationHint')}
                </p>
                <CapabilityValuesInput
                  id="duration_range"
                  label={t('gatewayModal.capDuration')}
                  values={stringCapabilityValues(capabilities, 'duration_range')}
                  onChange={(values) => handleCapabilityValuesChange('duration_range', values)}
                />
              </div>

              {LIST_CAPABILITY_OPTIONS.map(([key, labelKey]) => (
                <CapabilityValuesInput
                  key={key}
                  id={key}
                  label={t(labelKey, { appName: APP_NAME })}
                  values={stringCapabilityValues(capabilities, key)}
                  onChange={(values) => handleCapabilityValuesChange(key, values)}
                />
              ))}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <MaximumCapabilityInput
                  id="reference_images_support"
                  label={t('gatewayModal.capReferenceImagesMaximum')}
                  value={maximumCapabilityValue(
                    stringCapabilityValues(capabilities, 'reference_images_support'),
                  )}
                  onChange={(rawValue) => handleReferenceMaximumChange(
                    'reference_images_support',
                    rawValue,
                  )}
                />
                <MaximumCapabilityInput
                  id="reference_videos_support"
                  label={t('gatewayModal.capReferenceVideosMaximum')}
                  value={maximumCapabilityValue(
                    stringCapabilityValues(capabilities, 'reference_videos_support'),
                  )}
                  onChange={(rawValue) => handleReferenceMaximumChange(
                    'reference_videos_support',
                    rawValue,
                  )}
                />
                <MaximumCapabilityInput
                  id="reference_audios_support"
                  label={t('gatewayModal.capReferenceAudiosMaximum')}
                  value={maximumCapabilityValue(
                    stringCapabilityValues(capabilities, 'reference_audios_support'),
                  )}
                  onChange={(rawValue) => handleReferenceMaximumChange(
                    'reference_audios_support',
                    rawValue,
                  )}
                />
                <MaximumCapabilityInput
                  id="max_total_references"
                  label={t('gatewayModal.capReferenceTotalMaximum')}
                  value={maximumCapabilityValue(
                    stringCapabilityValues(capabilities, 'max_total_references'),
                  )}
                  onChange={(rawValue) => handleReferenceMaximumChange(
                    'max_total_references',
                    rawValue,
                  )}
                />
              </div>

            </div>
          </div>
        )}

        {isKnownVideoPreset && (
          <div className="rounded-xl border border-border-subtle bg-surface-1 px-4 py-3" data-testid="gateway-modal-preset-capabilities">
            <p className="text-xxs uppercase tracking-wider text-muted-foreground mb-2">
              {t('gatewayModal.presetCapabilities')}
            </p>
            <p className="text-sm text-foreground">
              {t('gatewayModal.museCapabilitiesSummary')}
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => { trackByomClick('save_clicked'); void handleSave() }}
            disabled={saveDisabled}
            className="px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
            data-testid="gateway-modal-save"
          >
            {saving ? t('gatewayModal.saving') : t(isEdit ? 'gatewayModal.saveChanges' : 'gatewayModal.addModel')}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-md border border-border bg-surface-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            data-testid="gateway-modal-cancel"
          >
            {t('gatewayModal.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
